import { Spin, Typography } from 'antd';
import type { ReactNode } from 'react';

import { AccessDenied } from './access-denied';

const { Text } = Typography;

export interface ProtectedRouteProps {
  children: ReactNode;
  /** Whether auth state is still loading */
  isLoading: boolean;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** User's current roles */
  roles?: string[];
  /**
   * Required roles for this route.
   * User must have AT LEAST ONE of the listed roles to access.
   */
  requiredRoles?: string[];
  /** Custom fallback component while loading */
  loadingFallback?: ReactNode;
  /** Component to render when access is denied */
  accessDeniedFallback?: ReactNode;
  /** Called when redirecting to login (for unauthenticated users) */
  onLoginRedirect?: () => void;
  /** Loading text to display */
  loadingText?: string;
  /** Redirecting text to display */
  redirectingText?: string;
}

/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Shows loading state while checking auth, and redirects unauthenticated users.
 *
 * This is a framework-agnostic component that accepts auth state as props.
 * Use it with your auth provider's hook (e.g., useAuth from @repo/auth).
 *
 * @example
 * ```tsx
 * function MyLayout({ children }) {
 *   const { isLoading, isAuthenticated, roles, hasAnyRole } = useAuth();
 *   const router = useRouter();
 *
 *   return (
 *     <ProtectedRoute
 *       isLoading={isLoading}
 *       isAuthenticated={isAuthenticated}
 *       roles={roles}
 *       requiredRoles={['admin']}
 *       onLoginRedirect={() => router.push('/login')}
 *       accessDeniedFallback={<AccessDenied onGoBack={() => router.back()} />}
 *     >
 *       {children}
 *     </ProtectedRoute>
 *   );
 * }
 * ```
 */
export function ProtectedRoute({
  children,
  isLoading,
  isAuthenticated,
  roles = [],
  requiredRoles,
  loadingFallback,
  accessDeniedFallback,
  onLoginRedirect,
  loadingText = 'Loading...',
  redirectingText = 'Redirecting to login...',
}: ProtectedRouteProps) {
  // Default loading component
  const defaultLoadingComponent = (text: string) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        gap: '16px',
      }}
    >
      <Spin size="large" />
      <Text type="secondary">{text}</Text>
    </div>
  );

  // Show loading state while checking auth
  if (isLoading) {
    return <>{loadingFallback || defaultLoadingComponent(loadingText)}</>;
  }

  // Not authenticated - trigger login redirect
  if (!isAuthenticated) {
    // Call the redirect callback (e.g., router.push('/login'))
    if (onLoginRedirect) {
      // Use setTimeout to avoid calling during render
      setTimeout(onLoginRedirect, 0);
    }
    return <>{loadingFallback || defaultLoadingComponent(redirectingText)}</>;
  }

  // Check required roles
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((role) => roles.includes(role));
    if (!hasRequiredRole) {
      return (
        <>
          {accessDeniedFallback || (
            <AccessDenied requiredRoles={requiredRoles} />
          )}
        </>
      );
    }
  }

  // Authenticated and has required roles - render children
  return <>{children}</>;
}

/**
 * Helper function to check if user has any of the required roles
 */
export function hasAnyRole(
  userRoles: string[],
  requiredRoles: string[],
): boolean {
  return requiredRoles.some((role) => userRoles.includes(role));
}

/**
 * Helper function to check if user has all of the required roles
 */
export function hasAllRoles(
  userRoles: string[],
  requiredRoles: string[],
): boolean {
  return requiredRoles.every((role) => userRoles.includes(role));
}
